import { useDispatch, useSelector } from 'react-redux';
import React, { useMemo } from 'react';
import Hotspot from 'threejs_scene/Hotspot';
import { formURL } from '@wsc/utils/apiUtils';
import config from 'config';
import PropTypes from 'prop-types';
import { extractNavHotspotInfo } from '@wsc/utils/sceneUtils';
import { useAnalytics, useLocalizedNavigation } from '@wsc/hooks';
import { createSelector } from '@reduxjs/toolkit';
import {
	createIsHotspotActiveAdaSelector,
	setAccessibilitySelector,
} from '@wsc/redux_stores/accessibilityReducer';
import { createRoomObjectDataSelector } from '@wsc/redux_stores/roomObjectsReducer';

const stylingSelector = createSelector(
	[(state) => state?.storeData],
	(storeData) => ({
		navigationArrowIcon:
			storeData?.styling?.icons?.nav_arrow_icon?.name || '',
		navigationArrowIconHover:
			storeData?.styling?.icons?.nav_arrow_icon_hover?.name || '',
		storeIconFiles: storeData?.styling?.store_icon_files || {},
		storeNavSoundFile: storeData?.sfx_assets?.navigation || {},
	}),
);

const sceneSelector = (currentSceneId, nextSceneId) =>
	createSelector([(state) => state?.scenes], (scenes) => ({
		currentSceneName: scenes?.[currentSceneId]?.name,
		nextSceneName: scenes?.[nextSceneId]?.name,
	}));

const NavMarker = ({ hotspotId, sceneId, ...props }) => {
	const {
		navigationArrowIcon,
		navigationArrowIconHover,
		storeIconFiles,
		storeNavSoundFile,
	} = useSelector(stylingSelector);

	const dataSelector = useMemo(
		() => createRoomObjectDataSelector(hotspotId),
		[hotspotId],
	);
	const hotspotData = useSelector(dataSelector);

	const adaActiveSelector = useMemo(
		() => createIsHotspotActiveAdaSelector(hotspotId),
		[hotspotId],
	);

	const isActiveAda = useSelector(adaActiveSelector);

	const { isEnabled: creatorToolsEnabled, hideNavigation } = useSelector(
		(state) => state?.creatorTools,
	);

	const { currentSceneName, nextSceneName } = useSelector(
		sceneSelector(sceneId, hotspotData?.props?.linked_room_id?.$oid),
	);

	const { navigate } = useLocalizedNavigation();

	const getNavMarkerImage = (type, arrowState = 'default') => {
		const arrowsMap = {
			stairs_up: 'stairs-up-black.svg',
			stairs_down: 'stairs-down-black.svg',
			up: 'arrow-black.svg',
		};

		let arrowKey = 'arrow.svg';
		if (type in arrowsMap) {
			arrowKey = arrowsMap[type];
		}
		const arrowUrl = `https://cdn.obsess-vr.com/${arrowKey}`;
		if (type === 'stairs_up' || type === 'stairs_down') {
			if (arrowState === 'hover') {
				if (hotspotData.props?.hover_icon) {
					return formURL(hotspotData.props.hover_icon);
				}
			} else {
				if (hotspotData.props?.icon) {
					return formURL(hotspotData.props.icon);
				}
			}
			return arrowUrl;
		}

		if (arrowState === 'hover') {
			if ('hover_icon' in hotspotData.props) {
				return formURL(hotspotData.props.hover_icon);
			}
			if (navigationArrowIconHover) {
				const fromIconFiles = formURL(
					storeIconFiles[navigationArrowIconHover]?.url,
				);
				if (fromIconFiles) {
					return fromIconFiles;
				}
			}
		}

		if (arrowState === 'default') {
			if ('icon' in hotspotData.props) {
				return formURL(hotspotData.props.icon);
			}
			if (navigationArrowIcon) {
				const fromIconFiles = formURL(
					storeIconFiles[navigationArrowIcon]?.url,
				);
				if (fromIconFiles) {
					return fromIconFiles;
				}
			}
		}

		return arrowUrl;
	};

	const getNavMarkerSound = () => {
		if (hotspotData.props?.sfx_asset) {
			return formURL(hotspotData.props.sfx_asset);
		} else if (storeNavSoundFile) {
			return formURL(storeNavSoundFile);
		}
		return '';
	};

	const dispatch = useDispatch();
	const { collect } = useAnalytics();
	const onClicked = () => {
		const hotspotInfo = extractNavHotspotInfo(hotspotData);
		dispatch(setAccessibilitySelector(''));
		collect({
			event_name: 'click_navigation',
			action: 'Floor arrow clicked',
			label: `${currentSceneName} > ${nextSceneName}`,
			...hotspotInfo,
		});

		navigate(nextSceneName);
	};

	const hideNavigationForCreatorTools = creatorToolsEnabled && hideNavigation;
	return (
		<Hotspot
			{...props}
			type="HotspotMarker"
			collider_transform={hotspotData.collider_transform}
			transform={hotspotData.transform}
			iconConfig={{
				showIcon: hideNavigationForCreatorTools
					? false
					: hotspotData?.props?.hasOwnProperty('show_icon')
					? hotspotData?.props?.show_icon
					: true,
			}}
			imageURL={getNavMarkerImage(
				hotspotData?.props?.sprite_type || '',
				'default',
			)}
			imageHoverURL={getNavMarkerImage(
				hotspotData?.props?.sprite_type || '',
				'hover',
			)}
			soundURL={getNavMarkerSound()}
			focusOnClick={hotspotData?.focus_on_click || false}
			userData={{
				...hotspotData,
			}}
			onClick={onClicked}
			isActive={isActiveAda}
			tooltip={
				hotspotData.props?.tooltip?.enabled
					? hotspotData.props.tooltip
					: { enabled: false }
			}
		/>
	);
};

NavMarker.propTypes = {
	hotspotData: PropTypes.object.isRequired,
};

export default NavMarker;
